<template>
  <div
    class="p-1.5 sm:p-2 rounded-2xl flex flex-col md:flex-row items-stretch md:items-center gap-2 border border-slate-100"
    :class="compact ? 'bg-surface-container-lowest shadow-sm' : 'bg-white shadow-2xl'">
    <div class="flex-1 flex items-center px-3 sm:px-4 gap-3 min-h-[52px] sm:min-h-[60px]" :class="compact ? '' : 'border-b md:border-b-0 md:border-r border-slate-100'">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M17.657 16.657L13.414 12.414A6 6 0 1112 13.414l4.243 4.243z" />
      </svg>

      <div class="w-full relative">
        <input type="text" v-model="searchQuery" @input="filterOptions" @focus="showDropdown = true" @blur="onBlur"
          class="w-full py-2.5 sm:py-3 bg-transparent border-none focus:ring-0 focus:outline-none text-slate-700 placeholder-slate-400 font-normal"
          :placeholder="placeholder" />

        <div v-if="selectedOptions.length > 0" class="flex items-center flex-wrap gap-2 pb-1">
          <UiChip
            v-if="selectedOptions.length === 1"
            variant="compact"
            :label="selectedOptions[0].nombre.length > 22 ? selectedOptions[0].nombre.substring(0, 22) + '…' : selectedOptions[0].nombre"
            :closable="true"
            @remove="removeTag(selectedOptions[0])"
            :clickable="false" />

          <UiChip
            v-else
            variant="compact"
            :label="`${selectedOptions.length} ubicaciones`"
            :closable="true"
            @remove="selectedOptions = []"
            :clickable="false" />
        </div>

        <div v-if="showDropdown && filteredOptions.length > 0"
          class="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          <div v-for="(option, index) in filteredOptions" :key="option.id" @mousedown.prevent="selectOption(option)"
            class="px-4 py-2 cursor-pointer hover:bg-slate-50 text-slate-700"
            :class="{ 'bg-slate-50': highlightedIndex === index }">
            {{ option.nombre }}
          </div>
        </div>

        <div v-if="showDropdown && searchQuery && filteredOptions.length === 0"
          class="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-4 text-slate-500">
          No se encontraron resultados
        </div>
      </div>
    </div>

    <div v-if="!compact" class="flex flex-1 flex-col md:flex-row items-stretch md:items-center gap-2">
      <div class="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 md:py-0 md:border-r border-slate-100">
        <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Operación</label>
        <select v-model="transactionType"
          class="w-full bg-transparent border-none p-0 focus:ring-0 focus:outline-none text-slate-700 font-medium cursor-pointer min-h-[44px]">
          <option value="venta">Venta</option>
          <option value="alquiler">Alquiler</option>
        </select>
      </div>

      <div class="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 md:py-0">
        <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Tipo</label>
        <select v-model="propertyType"
          class="w-full bg-transparent border-none p-0 focus:ring-0 focus:outline-none text-slate-700 font-medium cursor-pointer min-h-[44px]">
          <option value="departamento">Departamento</option>
          <option value="casa">Casa</option>
          <option value="local">Local</option>
          <option value="ph">PH</option>
          <option value="cochera">Cochera</option>
        </select>
      </div>
    </div>

    <button
      class="btn btn-primary w-full md:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
      :disabled="selectedOptions.length === 0" @click="redirectToSearch">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <span>{{ buttonText }}</span>
    </button>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { parseFiltersFromUrl, buildFilters } from '@/utils/filters';
import UiChip from '@/components/common/ui/UiChip.vue';

export default {
  name: 'SearchBar',
  props: {
    compact: {
      type: Boolean,
      default: false
    }
    ,
    autoSearchOnChange: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Select: () => import('../../components/common/Select.vue'),
    UiChip
  },
  data() {
    return {
      options: [],
      searchQuery: '',
      filteredOptions: [],
      showDropdown: false,
      highlightedIndex: -1,
      placeholder: 'Buenos Aires, CABA, La Plata',
      buttonText: 'Buscar',
      selectedOptions: [],
      propertyType: "casa",
      transactionType: "venta",
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const selectedOption = this.options.find(option => option.value === newValue);
          if (selectedOption) {
            this.searchQuery = selectedOption.label;
          }
        }
      }
    },
    options: {
      immediate: true,
      handler(newOptions) {
        this.filterOptions();
      }
    },
    '$route.query': {
      handler(newFilters, oldFilters) {
        const filtros = parseFiltersFromUrl(newFilters);
        this.propertyType = filtros.property_type || 'casa';
        this.transactionType = filtros.transaction_type || 'venta';
        this.selectedOptions = filtros.locations || [];
      },
      immediate: true
    },
    propertyType: {
      handler(newValue, oldValue) {
        if (oldValue && newValue !== oldValue) {
          if (this.autoSearchOnChange) this.redirectToSearch();
        }
      }
    },
    transactionType: {
      handler(newValue, oldValue) {
        if (oldValue && newValue !== oldValue) {
          if (this.autoSearchOnChange) this.redirectToSearch();
        }
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);
    const filtros = parseFiltersFromUrl(this.$route.query);
    this.propertyType = filtros.property_type || 'casa';
    this.transactionType = filtros.transaction_type || 'venta';
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    ...mapActions({
      searchLocations: 'SEARCH_LOCATIONS',
    }),
    async filterOptions() {
      if (!this.searchQuery) {
        this.filteredOptions = [...this.options];
        return;
      }

      this.filteredOptions = await this.searchLocations(this.searchQuery);
      this.highlightedIndex = this.filteredOptions.length > 0 ? 0 : -1;
    },
    selectOption(option) {
      this.selectedOptions.push(option);
      this.searchQuery = '';
      this.options = [];
    },
    onBlur() {
      // Delay hiding dropdown to allow click events to complete
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },
    handleKeyDown(e) {
      if (!this.showDropdown) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.highlightedIndex = Math.min(
            this.highlightedIndex + 1,
            this.filteredOptions.length - 1
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          break;
        case 'Enter':
          if (this.highlightedIndex >= 0) {
            e.preventDefault();
            this.selectOption(this.filteredOptions[this.highlightedIndex]);
          }
          break;
        case 'Escape':
          this.showDropdown = false;
          break;
      }
    },
    redirectToSearch() {
      const { queryString } = buildFilters({
        locations: this.selectedOptions,
        property_type: this.propertyType,
        transaction_type: this.transactionType,
      });

      const targetRoute = `/search?${queryString}`;
      
      if (this.$route.fullPath !== targetRoute) {
        this.$router.push(targetRoute);
      } else {
        this.$emit('search', {
          locations: this.selectedOptions,
          property_type: this.propertyType,
          transaction_type: this.transactionType,
        });
      }
    },
    
    removeTag(option) {
      const index = this.selectedOptions.findIndex(selected => selected.id === option.id);
      if (index > -1) {
        this.selectedOptions.splice(index, 1);
      }
    }
  },
  computed: {
  },
}
</script>
